import { jsx as _jsx } from "react/jsx-runtime";
import { getCheckboxClasses } from '#pplx/frontend/aether/components/Checkbox/classes.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
const CHECKBOX_INDICATOR_ICON_SIZE = {
    large: 'small',
    default: 'small',
    small: 'tiny',
};
/**
 * Accessible Radix checkbox wrapper for checked and indeterminate form
 * states, with size variants, disabled state, and explicit labeling
 * requirements.
 *
 * @category components
 * @keywords checkbox, components, radix, accessible, checked, indeterminate,
 *   state, size, disabled, label, aria-label, aria-labelledby, switch,
 *   radio-group, chip
 * @whenToUse Use Checkbox when users need to select one or more options from
 *   a list, or to toggle a binary on/off state in a form that will be
 *   submitted. Checkboxes are ideal for settings forms, multi-select filters,
 *   terms-of-service agreements, and any scenario where selections are
 *   collected and submitted together. For instant toggles that take effect
 *   immediately, use Switch instead.
 * @guidelineDo
 *   - Always give the checkbox an accessible name that clearly describes what it controls — via
 *     aria-label, aria-labelledby referencing a visible label element, or an id paired with a
 *     native label element
 *   - Use onCheckedChange to respond to state changes — the callback receives the new boolean
 *     checked value directly
 *   - Use the default size for standard forms and the small size for compact contexts like table
 *     rows or menu items
 *   - Pair the checkbox with a visible text label in the UI layout so sighted users understand
 *     what it controls
 *   - Use a fieldset and legend when grouping multiple checkboxes for a common purpose
 * @guidelineDont
 *   - Don't use Checkbox for mutually exclusive choices — use RadioGroup instead for
 *     single-selection from a set
 *   - Don't use Checkbox as a toggle that takes immediate effect — use Switch for instant on/off
 *     changes; reserve Checkbox for deferred submission via a form
 *   - Don't override tabIndex unless building a composite widget like a DropdownMenu with checkbox
 *     items — it is marked as internal
 *   - Don't rely solely on the checked color to communicate state — the checkmark icon provides a
 *     secondary visual indicator
 * @accessibility
 *   - Provides the checkbox ARIA role, aria-checked state management, and keyboard toggling via
 *     Space key
 *   - The accessible name comes from exactly one labeling path: aria-label when no visible label
 *     is rendered, aria-labelledby referencing a visible label element, or an id paired with a
 *     native label element — there is no built-in visible label
 *   - The checkmark indicator uses the IconSprite check icon which is visually centered inside the
 *     checkbox for both default and small sizes
 *   - Disabled checkboxes reduce opacity to 50% and set cursor-default, with the disabled
 *     attribute preventing interaction and communicating the state to assistive technology
 *   - The interactable focus ring is applied via the 'reset interactable' class on the root
 *     element, providing a visible keyboard focus indicator
 * @relatedComponents
 *   - switch
 *   - radio-group
 *   - chip
 */
export function Checkbox({ checked = false, onCheckedChange, id, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, size = 'default', disabled = false, tabIndex = 0, }) {
    const indicatorIcon = checked === 'indeterminate' ? $icon('minus') : $icon('check');
    return (_jsx(CheckboxPrimitive.Root, { checked: checked, onCheckedChange: (nextChecked) => onCheckedChange(nextChecked === true), disabled: disabled, id: id, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, className: getCheckboxClasses({ size, disabled, checked }), tabIndex: tabIndex, children: _jsx(CheckboxPrimitive.Indicator, { className: "text-inverse flex items-center justify-center", children: _jsx(Icon, { icon: indicatorIcon, size: CHECKBOX_INDICATOR_ICON_SIZE[size] }) }) }));
}
//# sourceMappingURL=index.js.map